// @ts-check
/* ═══════════════════════ PWA — install-prompt + notifikasjoner ═══════════════════════
   - Fanger beforeinstallprompt på Android/desktop, viser banner.
   - iOS Safari mangler API; viser i stedet en hint med "Del → Legg til på Hjem-skjerm".
   - Notifikasjons-tillatelse + scheduling via service worker (best effort, faller pent).
   ════════════════════════════════════════════════════════════════════════════════ */

const LS_DISMISSED = "pwa_install_dismissed_v1";
const LS_NOTIF_ASKED = "pwa_notif_asked_v1";

let deferredPrompt = null;

function isInstalled(){
  return window.matchMedia('(display-mode: standalone)').matches
      || window.navigator.standalone === true;
}

function isIOS(){
  return /iphone|ipad|ipod/i.test(navigator.userAgent) && !window.MSStream;
}

function bannerHTML(mode){
  const dismiss = `<button class="pwa-x" aria-label="Lukk" onclick="window.dismissInstall()">×</button>`;
  if (mode === 'ios') {
    return `<div class="pwa-install" role="dialog" aria-label="Installer app">
      <div class="pwa-ic">📲</div>
      <div class="pwa-tx">
        <div class="pwa-h">Legg til på Hjem-skjerm</div>
        <div class="pwa-s">Trykk <strong>Del</strong> i Safari, så <strong>«Legg til på Hjem-skjerm»</strong>.</div>
      </div>
      ${dismiss}
    </div>`;
  }
  return `<div class="pwa-install" role="dialog" aria-label="Installer app">
    <div class="pwa-ic">📲</div>
    <div class="pwa-tx">
      <div class="pwa-h">Installer reiseappen</div>
      <div class="pwa-s">Få den som ekte app — fungerer offline på reisen.</div>
    </div>
    <button class="pwa-go" onclick="window.acceptInstall()">Installer</button>
    ${dismiss}
  </div>`;
}

function ensureContainer(){
  let el = document.getElementById('pwa-install-root');
  if (!el) {
    el = document.createElement('div');
    el.id = 'pwa-install-root';
    document.body.appendChild(el);
  }
  return el;
}

function showBanner(mode){
  const el = ensureContainer();
  el.innerHTML = bannerHTML(mode);
}

function hideBanner(){
  const el = document.getElementById('pwa-install-root');
  if (el) el.innerHTML = '';
}

export function dismissInstall(){
  try { localStorage.setItem(LS_DISMISSED, String(Date.now())); } catch {}
  hideBanner();
}

export async function acceptInstall(){
  if (!deferredPrompt) return;
  hideBanner();
  try {
    deferredPrompt.prompt();
    await deferredPrompt.userChoice;
  } catch {}
  deferredPrompt = null;
}

export function initPWA(){
  if (isInstalled()) return;

  // Vent 8 s før vi nag-er — rolig oppstart, så banner.
  const dismissedAt = parseInt(localStorage.getItem(LS_DISMISSED) || '0', 10);
  const SUPPRESS_MS = 7 * 24 * 60 * 60 * 1000; // 7 dager
  if (Date.now() - dismissedAt < SUPPRESS_MS) return;

  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    deferredPrompt = e;
    setTimeout(() => showBanner('default'), 8000);
  });

  window.addEventListener('appinstalled', () => {
    deferredPrompt = null;
    hideBanner();
    try { localStorage.setItem(LS_DISMISSED, String(Date.now())); } catch {}
  });

  // iOS har ikke beforeinstallprompt — vis hint en gang i Safari etter 12 s
  if (isIOS()) {
    setTimeout(() => { if (!isInstalled()) showBanner('ios'); }, 12000);
  }

  // Eksponer handlers for inline onclick
  window.dismissInstall = dismissInstall;
  window.acceptInstall = acceptInstall;
}

/* ── Notifikasjoner (V5b) ── */

export async function askNotifPermission(){
  if (!('Notification' in window)) return 'unsupported';
  if (Notification.permission === 'granted') return 'granted';
  if (Notification.permission === 'denied') return 'denied';
  try {
    const p = await Notification.requestPermission();
    try { localStorage.setItem(LS_NOTIF_ASKED, '1'); } catch {}
    return p;
  } catch { return 'error'; }
}

/** Send en notifikasjon nå (krever tillatelse). */
export async function notify(title, options = {}){
  if (!('Notification' in window) || Notification.permission !== 'granted') return false;
  try {
    const reg = await navigator.serviceWorker?.ready;
    if (reg) {
      await reg.showNotification(title, { icon: '/icons/icon.svg', badge: '/icons/icon.svg', ...options });
    } else {
      new Notification(title, options);
    }
    return true;
  } catch { return false; }
}
